import { FormEvent, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { CircularProgress } from '@mui/material'

import { initializeKeycloak } from '../../redux/keycloak/login'
import { useAppDispatch } from '../../redux/store'
import { resolveTenantApi } from '../../api/tenantApi'
import {
  selectLoading,
  selectError,
} from '../../redux/keycloak/keycloakSelectors'
import { createSanitizedHandler } from '../../utils/inputHandlers'
import { AuthPasswordInput, AuthShell } from './AuthShell'

const Login = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [keepSignedIn, setKeepSignedIn] = useState(false)
  const handleUsernameChange = createSanitizedHandler(setUsername, 'username')

  const organizationSlug = searchParams.get('org') || ''
  const loadingLogin = useSelector(selectLoading)
  const error = useSelector(selectError)

  const handleLogin = async (e: FormEvent): Promise<void> => {
    e.preventDefault()

    const tenant = organizationSlug
      ? await resolveTenantApi(organizationSlug.trim().toLowerCase())
      : undefined

    if (keepSignedIn) {
      try {
        localStorage.setItem('incyia.keepSignedIn', '1')
      } catch {
        /* ignore */
      }
    }

    await dispatch(
      initializeKeycloak({
        username,
        password,
        clientId: tenant?.keycloakClientId,
      })
    )
  }

  return (
    <AuthShell
      foot={
        <>
          {t('auth.noAccountYet')}{' '}
          <button
            type="button"
            className="incyia-auth-link"
            onClick={() => navigate('/register')}
          >
            {t('auth.createOne')}
          </button>
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              className="incyia-auth-link"
              onClick={() => navigate('/get-started')}
            >
              {t('auth.createOrganization')}
            </button>
          </div>
        </>
      }
    >
      <h1 className="incyia-auth-title">{t('auth.signIn')}</h1>
      <p className="incyia-auth-sub">{t('auth.signInSub')}</p>

      {error ? <div className="incyia-auth-error">{error}</div> : null}

      <form onSubmit={handleLogin}>
        <div className="incyia-auth-field">
          <label className="incyia-auth-label" htmlFor="login-username">
            {t('auth.usernameOrEmail')}
            <span className="incyia-auth-req">*</span>
          </label>
          <input
            id="login-username"
            className="incyia-auth-inp"
            placeholder={t('auth.usernamePlaceholder')}
            autoComplete="username"
            value={username}
            onChange={handleUsernameChange}
            required
          />
        </div>

        <div className="incyia-auth-field">
          <label className="incyia-auth-label" htmlFor="login-password">
            {t('auth.password')}
            <span className="incyia-auth-req">*</span>
          </label>
          <AuthPasswordInput
            id="login-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••"
            autoComplete="current-password"
            required
          />
        </div>

        <div className="incyia-auth-row">
          <label className="incyia-auth-check">
            <input
              type="checkbox"
              checked={keepSignedIn}
              onChange={(e) => setKeepSignedIn(e.target.checked)}
            />
            <span>{t('auth.keepSignedIn')}</span>
          </label>
          <button
            type="button"
            className="incyia-auth-link"
            onClick={() => navigate('/forgot-password')}
          >
            {t('auth.forgotPassword')}
          </button>
        </div>

        <button
          type="submit"
          className="incyia-auth-submit"
          disabled={loadingLogin}
        >
          {loadingLogin ? (
            <CircularProgress size={20} sx={{ color: '#fff' }} />
          ) : (
            t('auth.signIn')
          )}
        </button>
      </form>

      <div className="incyia-auth-trail">
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden
        >
          <circle cx="8" cy="8" r="5.5" />
          <path d="M8 7.2v3.5M8 5.2h.01" strokeLinecap="round" />
        </svg>
        <span>{t('auth.signInAuditNote')}</span>
      </div>
    </AuthShell>
  )
}

export default Login
